/**
 * The caller ID name the phone network sends (CNAM): up to 15 capitals, often "SMITH JOHN" or
 * "JOHN SMITH" (which comes first varies by carrier), sometimes a place ("OKLAHOMA CTY OK") or
 * nothing useful ("WIRELESS CALLER"). Only ever a suggestion for staff to confirm.
 */

const STATES = new Set("AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" "));

const NOT_A_NAME = /^(wireless caller|unknown( caller| name)?|unavailable|private( caller| number)?|anonymous|restricted|blocked|no caller id|toll free( call)?|out of area|cell phone|mobile( caller)?|v?oip( caller)?)$/i;

/** "o'BRIEN-smith" → "O'Brien-Smith"; "MCDONALD" → "McDonald". */
export function prettyName(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/(^|[\s'’-])([a-z])/g, (_, sep: string, c: string) => sep + c.toUpperCase())
    .replace(/\bMc([a-z])/g, (_, c: string) => `Mc${c.toUpperCase()}`);
}

/**
 * A person's name from caller ID, or null when it isn't one (a place, "WIRELESS CALLER", a
 * business-looking number). "SMITH, JOHN" is last-first; otherwise the first word is taken as the
 * first name (staff can swap them).
 */
export function callerIdName(raw: string | null | undefined): { firstName: string; lastName: string } | null {
  const s = (raw ?? "").trim().replace(/\s+/g, " ");
  if (!s || NOT_A_NAME.test(s) || /\d/.test(s)) return null;
  // A city and state ("OKLAHOMA CTY OK", "NORMAN OK").
  if (s === s.toUpperCase() && STATES.has(s.slice(-3).trim()) && / [A-Z]{2}$/.test(s)) return null;
  if (s.includes(",")) {
    const [last, first] = s.split(",").map((x) => x.trim());
    if (!first || !last) return null;
    return { firstName: prettyName(first.split(" ")[0]!), lastName: prettyName(last) };
  }
  const words = s.split(" ");
  if (words.length < 2) return null;
  return { firstName: prettyName(words[0]!), lastName: prettyName(words.slice(1).join(" ")) };
}
